import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { makeHighlightRenderer } from './pdfHighlight';

// Bundled worker: the CSP allows workers from 'self' only.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
// No new Function() in pdf.js under the CSP (script-src without 'unsafe-eval').
const PDF_OPTIONS = { isEvalSupported: false };

function PdfViewer({ pdfUrl, targetPage, targetPageEnd, highlightContent }) {
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const onLoadSuccess = useCallback(({ numPages }) => setNumPages(numPages), []);

  // Highlight text items that appear in the top chunk
  const textRenderer = useMemo(() => makeHighlightRenderer(highlightContent), [highlightContent]);
  const startPage = Math.max(1, targetPage || 1);
  const endPage = Math.max(startPage, Math.min(numPages || targetPageEnd || startPage, targetPageEnd || startPage));
  const pages = [];
  for (let p = startPage; p <= endPage; p++) pages.push(p);

  const indicator = pages.length > 1
    ? `Pages ${startPage}–${endPage}${numPages ? ` of ${numPages}` : ''}`
    : `Page ${startPage}${numPages ? ` of ${numPages}` : ''}`;

  return (
    <div className="pdf-viewer-container" ref={containerRef}>
      {numPages && (
        <div className="pdf-page-indicator">
          {indicator}
          {targetPage > 1 && (
            <span className="pdf-page-note"> — jumped to relevant section</span>
          )}
        </div>
      )}
      <Document
        file={pdfUrl}
        options={PDF_OPTIONS}
        onLoadSuccess={onLoadSuccess}
        loading={<div className="pdf-loading">Loading PDF…</div>}
        error={<div className="pdf-error">Could not load PDF.</div>}
      >
        {pages.map(p => (
          <Page
            key={p}
            pageNumber={p}
            width={containerWidth || undefined}
            customTextRenderer={textRenderer}
            renderAnnotationLayer={false}
          />
        ))}
      </Document>
    </div>
  );
}

function ChunkList({ chunks }) {
  return (
    <div className="source-pane-chunks">
      {chunks.map((chunk, i) => (
        <div key={i} className={`source-chunk${i === 0 ? ' source-chunk-primary' : ''}`}>
          <div className="source-chunk-meta">
            {chunk.section_anchor && (
              <span className="source-chunk-anchor">§ {chunk.section_anchor}</span>
            )}
            {chunk.chapter && (
              <span className="source-chunk-chapter">{chunk.chapter}</span>
            )}
            {chunk.page && (
              <span className="source-chunk-page">p. {chunk.page}</span>
            )}
          </div>
          <p className="source-chunk-text">{chunk.content}</p>
        </div>
      ))}
    </div>
  );
}

export default function SourcePane({ dialogueId, cardIndex, collapsed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('pdf'); // 'pdf' | 'text'

  useEffect(() => {
    if (collapsed || !dialogueId) return;
    setLoading(true);
    setData(null);
    fetch(`/api/learning/dialogue/${dialogueId}/source`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dialogueId, cardIndex, collapsed]);

  if (collapsed) return null;

  if (loading) return <div className="source-pane source-pane-loading">Loading source…</div>;

  if (!data || !data.chunks?.length) {
    return <div className="source-pane source-pane-empty">No source available</div>;
  }

  const showPdf = data.hasPdf && data.pdfUrl && viewMode === 'pdf';
  const topChunk = data.chunks[0];
  const targetPage = topChunk?.page || 1;
  const targetPageEnd = topChunk?.page_end || targetPage;

  return (
    <div className="source-pane">
      <div className="source-pane-header">
        <span className="source-pane-label">Source</span>
        <div className="source-pane-controls">
          {data.hasPdf && data.pdfUrl && (
            <>
              <button
                className={`source-view-toggle${viewMode === 'pdf' ? ' active' : ''}`}
                onClick={() => setViewMode('pdf')}
              >
                PDF
              </button>
              <button
                className={`source-view-toggle${viewMode === 'text' ? ' active' : ''}`}
                onClick={() => setViewMode('text')}
              >
                Text
              </button>
            </>
          )}
          {data.hasPdf && data.pdfUrl && (
            <a
              className="source-view-pdf-btn"
              href={data.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              ↗
            </a>
          )}
        </div>
      </div>

      {showPdf ? (
        <PdfViewer
          pdfUrl={data.pdfUrl}
          targetPage={targetPage}
          targetPageEnd={targetPageEnd}
          highlightContent={topChunk?.content}
        />
      ) : (
        <ChunkList chunks={data.chunks} />
      )}
    </div>
  );
}
